import React from 'react';
import {
  Box,
  BoxProps,
  Button,
  ButtonBase,
  Chip,
  Divider,
  DividerProps,
  IconProps,
  Stack,
  StackProps,
  SxProps,
  Typography,
  TypographyProps,
  darken,
} from '@mui/material';
import { Link } from 'react-router-dom';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { getSubjectColorNew, isMobileScreen } from '../../../../helper';
import { useAppSelector } from '../../../../hooks';
import { TP_COLORS } from '../../../../constants';

import {
  IGroupedSubTopic,
  IGroupedTopic,
} from '../../../../store/SubTopicSlice';
import { getProgressState } from '../../../../store/ProgressSlice';
import {
  checkQuizzes,
  checkResources,
  checkTopicPlanner,
  checkVideos,
  ChipNew,
} from '../../Subject/components/SubTopicList';
import CircularProgressWithValue from '../../UserDashboard/components/CircularProgressWithValue';
import { getSubTopicUrl } from '../../Resources';

const subTopicListStackProps: StackProps = {
  component: 'section',
  borderRadius: '20px',
  padding: isMobileScreen() ? '10px' : '15px 0px',
};

const dividerProps: DividerProps = {
  flexItem: true,
  orientation: 'vertical',
  variant: 'fullWidth',
  sx: { margin: '3px 10px' },
};

const subTopicItemBoxProps: BoxProps['sx'] = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '5px 20px',
};

const checkIconProps: IconProps['sx'] = {
  fontSize: 'medium',
  style: {
    height: '30px',
    width: '30px',
  },
};

const chipStyles: SxProps = {
  position: 'relative',
  marginLeft: '10px',
  borderRadius: '4px',
  fontSize: '12px',
  color: 'transparent',
  p: '0px 3px',
  backgroundImage: 'linear-gradient(90deg, #DB86F8 0%, #006FD6 100%)',
  backgroundClip: 'text',
  textFillColor: 'transparent',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '4px',
    padding: '1px',
    background: 'linear-gradient(90deg, #DB86F8 0%, #006FD6 100%)',
    WebkitMask:
      'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
  },
};

const boxProps: BoxProps = {
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const CircularProgressWithContent = ({ content, value, subjectColor }) => {
  return (
    <Box position="relative" display="inline-flex">
      <CircularProgressWithValue
        value={value || 0}
        progressProps={{ color: subjectColor }}
        thickness={3}
        circleColor={`${subjectColor}3A`}
      />
      <Box {...boxProps}>
        <Typography
          fontWeight="bold"
          fontSize="10px"
          component="div"
          color={subjectColor}
        >
          {content}
        </Typography>
      </Box>
    </Box>
  );
};

const optionTypoProps: TypographyProps = {
  component: 'p',
  variant: 'subtitle2',
  color: '#647488',
};

const subHeaderProps: TypographyProps = {
  component: 'h2',
  fontWeight: 'bold',
  fontSize: '18px',
};

interface SubTopicListProps {
  selectedTopic: IGroupedTopic;
}

const SubTopicList: React.FC<SubTopicListProps> = ({ selectedTopic }) => {
  const isMobile = isMobileScreen();

  // Store
  const progressState = useAppSelector(getProgressState);

  const { subjectProgress } = progressState;

  const subjectColor = getSubjectColorNew(selectedTopic.subject);

  const getSubTopicNavigateUrl = (subTopicData: IGroupedSubTopic) => {
    return `${getSubTopicUrl(subTopicData, selectedTopic)}${
      subTopicData.resources?.length ? '' : '/quiz'
    }`;
  };

  const getSubTopicProgress = (topicId: string, subTopicId: string) => {
    let isCompleted = false;
    let completionPercentage = 0;

    if (subjectProgress) {
      const topicProgress = subjectProgress.topics.find(
        (item) => item._id === topicId
      );

      if (topicProgress && topicProgress.subTopics.length) {
        const subTopicProgress = topicProgress.subTopics.find(
          (item) => item._id === subTopicId
        );

        if (subTopicProgress && subTopicProgress.completion !== undefined) {
          completionPercentage = Math.round(subTopicProgress.completion);
          isCompleted = completionPercentage === 100;
        }
      }
    }

    return { isCompleted, completionPercentage };
  };

  const getButtonText = (subTopic: IGroupedSubTopic) => {
    if (selectedTopic) {
      const subTopicProgress = getSubTopicProgress(
        selectedTopic._id,
        subTopic._id
      );
      if (subTopicProgress.isCompleted) {
        return 'Explore Again';
      }
    }

    if (
      subTopic.name === 'Topic Planner' ||
      (selectedTopic && selectedTopic.name === 'Overview')
    ) {
      return 'Overview';
    }

    if (checkDisabled(subTopic)) {
      return 'Upcoming...';
    }

    return 'Start Learning';
  };

  const checkDisabled = (subTopic: IGroupedSubTopic) => {
    if (
      !checkResources(subTopic) &&
      !checkVideos(subTopic) &&
      !checkQuizzes(subTopic)
    ) {
      return true;
    }

    return false;
  };

  const pastPapers = selectedTopic.subTopics.filter((subTopic) =>
    subTopic.name.toLowerCase().includes('past paper')
  );
  const worksheets = selectedTopic.subTopics.filter((subTopic) =>
    subTopic.name.toLowerCase().includes('worksheet')
  );
  const flashcards = selectedTopic.subTopics.filter((subTopic) =>
    subTopic.name.toLowerCase().includes('flashcard')
  );
  const subTopics = selectedTopic.subTopics.filter(
    (subTopic) =>
      !pastPapers.includes(subTopic) &&
      !worksheets.includes(subTopic) &&
      !flashcards.includes(subTopic)
  );

  const renderSubTopicItem = (
    subTopicItem: IGroupedSubTopic,
    subTopicIndex: number
  ) => {
    const { isCompleted, completionPercentage } = getSubTopicProgress(
      selectedTopic._id,
      subTopicItem._id
    );
    const hasResources = checkResources(subTopicItem);
    const hasQuizzes = checkQuizzes(subTopicItem);
    const hasVideos = checkVideos(subTopicItem);
    const hasPracticeQuestions = (subTopicItem.questionsCount ?? 0) > 4;

    return (
      <div key={subTopicIndex}>
        {hasResources || hasQuizzes || hasVideos || hasPracticeQuestions ? (
          <ButtonBase
            component={Link}
            to={getSubTopicNavigateUrl(subTopicItem)}
            disabled={checkDisabled(subTopicItem)}
            sx={{
              display: 'block',
              width: '100%',
              backgroundColor: 'transparent',
              borderBottom: '1px solid #CBD5E1',
              '&:hover': {
                backgroundColor: '#E2E8F04A',
              },
              '&:last-of-type': {
                borderBottom: 'none',
              },
            }}
          >
            <Box sx={subTopicItemBoxProps}>
              <Box display="flex" alignItems="center">
                {isCompleted ? (
                  <>
                    <CheckCircleIcon
                      sx={{ color: subjectColor }}
                      {...checkIconProps}
                    />
                  </>
                ) : (
                  <CircularProgressWithContent
                    value={completionPercentage}
                    content={`${completionPercentage}%`}
                    subjectColor={subjectColor}
                  />
                )}
                <Stack
                  padding="8px"
                  sx={{
                    marginLeft: {
                      sm: '30px',
                      md: '20px',
                    },
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <Typography
                      component="h3"
                      variant="subtitle1"
                      color={TP_COLORS.gray.darken}
                      sx={{ fontSize: { xs: '16px', sm: '18px' } }}
                    >
                      {subTopicItem.name}
                    </Typography>
                  </Box>
                  <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
                    {hasResources && (
                      <Typography {...optionTypoProps}>Resources</Typography>
                    )}

                    {checkTopicPlanner(subTopicItem.name) && (
                      <>
                        {hasVideos && (
                          <>
                            <Divider {...dividerProps} />
                            <Typography {...optionTypoProps}>Videos</Typography>
                          </>
                        )}
                        {hasResources && hasPracticeQuestions && (
                          <>
                            {(hasResources || hasVideos) && (
                              <Divider {...dividerProps} />
                            )}
                            <Typography {...optionTypoProps}>
                              Practice Question
                            </Typography>
                          </>
                        )}
                        {hasQuizzes && (
                          <>
                            {(hasResources || hasVideos) && (
                              <Divider {...dividerProps} />
                            )}
                            <Typography {...optionTypoProps}>
                              Quizzes
                            </Typography>
                            <Chip
                              size="small"
                              label="AI Powered"
                              sx={chipStyles}
                            />
                          </>
                        )}
                        {checkDisabled(subTopicItem) && (
                          <div style={{ height: '10px' }} />
                        )}
                      </>
                    )}
                  </Box>
                </Stack>
              </Box>

              <Button
                size={isMobile ? 'small' : 'medium'}
                color="secondary"
                variant="contained"
                disabled={checkDisabled(subTopicItem)}
                sx={{
                  minWidth: 'fit-content',
                  width: isMobile ? '110px' : '135px',
                  backgroundColor: subjectColor,
                  '&:hover': {
                    backgroundColor: darken(subjectColor, 0.3),
                  },
                }}
              >
                {getButtonText(subTopicItem)}
              </Button>
            </Box>
          </ButtonBase>
        ) : null}
      </div>
    );
  };

  return (
    <>
      {/* SubTopics Stack */}
      <Stack
        id="subtopics"
        {...subTopicListStackProps}
        sx={{ backgroundColor: TP_COLORS.white }}
      >
        {subTopics.map(renderSubTopicItem)}
      </Stack>

      {/* Past Papers Stack */}
      {pastPapers.length ? (
        <Stack
          id="topical-past-papers"
          {...subTopicListStackProps}
          sx={{ mt: '15px', backgroundColor: TP_COLORS.white }}
        >
          <Box
            padding="15px 20px"
            display="flex"
            flexDirection="row"
            alignItems="center"
          >
            <Typography {...subHeaderProps}>Topical Past Paper</Typography>
            <ChipNew />
          </Box>

          {pastPapers.map(renderSubTopicItem)}
        </Stack>
      ) : null}

      {/* Worksheets Stack */}
      {worksheets.length ? (
        <Stack
          id="worksheets"
          {...subTopicListStackProps}
          sx={{ mt: '15px', backgroundColor: TP_COLORS.white }}
        >
          <Typography {...subHeaderProps} padding="15px 20px">
            Worksheet
          </Typography>
          {worksheets.map(renderSubTopicItem)}
        </Stack>
      ) : null}

      {/* Flashcards Stack */}
      {flashcards.length ? (
        <Stack
          id="flashcards"
          {...subTopicListStackProps}
          sx={{ mt: '15px', backgroundColor: TP_COLORS.white }}
        >
          <Typography {...subHeaderProps} padding="15px 20px">
            FlashCard
          </Typography>
          {flashcards.map(renderSubTopicItem)}
        </Stack>
      ) : null}
      <div style={{ height: !isMobile ? '300px' : '' }} />
    </>
  );
};

export default SubTopicList;
